import { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { useNavigate } from "react-router-dom"; // Add this import
import DataTableMUI from "../../components/common/DataTable";
import { teamMemberColumns } from "../../services/tableColomn";
import {
  deleteTeamMembers,
  getAllTeamMembers,
  undoDeleteTeamMembers,
  updateTeamMemberOrder,
} from "../../redux/slices/teamMemberSlice";
import ViewDataPopup from "./viewDataPopup";
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";

const statusOptions = [
  { value: "all", label: "ALL", color: "#2563eb" },
  { value: "active", label: "ACTIVE", color: "#388e3c" },
  { value: "inactive", label: "INACTIVE", color: "#bfa100" },
  { value: "deleted", label: "DELETED", color: "#d32f2f" },
];

const TeamMember = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate(); // Add this
  const { teamMember, loading, statusCounts, pagination } = useAppSelector(
    (state) => state.teamMember
  );

  // Remove showForm and editData states since we're navigating
  const [viewPopupOpen, setViewPopupOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortKey, setSortKey] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    dispatch(
      getAllTeamMembers({
        status: statusFilter,
        page,
        search,
        limit: itemsPerPage,
        sortKey,
        sortOrder,
      })
    );
  }, [dispatch, statusFilter, page, search, itemsPerPage, sortKey, sortOrder]);

  const refetch = () =>
    dispatch(
      getAllTeamMembers({
        status: statusFilter,
        page,
        search,
        limit: itemsPerPage,
        sortKey,
        sortOrder,
      })
    );

  const handleStatusChange = (status) => {
    setStatusFilter(status);
    setPage(1);
  };

  const handlePageChange = (newPage) => setPage(newPage);
  
  const handleView = (row) => {
    setSelectedRow(row);
    setViewPopupOpen(true);
  };

  // Update handleEdit to navigate with data
  const handleEdit = (row) => {
    navigate("/team-member-form", { 
      state: { editData: row } 
    });
  };

  const handleDelete = async (row) => {
    if (window.confirm("Are you sure you want to delete this team member?")) {
      try {
        await dispatch(deleteTeamMembers(row._id));
        refetch();
      } catch (error) {
        console.error("Delete failed:", error);
      }
    }
  };

  const handleUndo = async (row) => {
    await dispatch(undoDeleteTeamMembers(row._id));
    refetch();
  };

  const handleSort = (col) => {
    if (!col.sortable) return;
    setSortKey(col.accessor);
    setSortOrder(
      sortKey === col.accessor && sortOrder === "asc" ? "desc" : "asc"
    );
    setPage(1);
  };

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 8,
      },
    })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = teamMember.findIndex((item) => item._id === active.id);
      const newIndex = teamMember.findIndex((item) => item._id === over.id);

      const reorderedItems = arrayMove(teamMember, oldIndex, newIndex).map(
        (item, index) => ({
          _id: item._id,
          position: index,
        })
      );

      try {
        await dispatch(updateTeamMemberOrder(reorderedItems));
        refetch();
      } catch (error) {
        console.error("Failed to update order:", error);
      }
    }
  };

  const statusOpts = statusOptions.map((opt) => ({
    ...opt,
    count: statusCounts?.[opt.value] || 0,
  }));

  return (
    <div className="data-table">
      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <DataTableMUI
          title="Team Members"
          columns={teamMemberColumns}
          data={teamMember || []}
          statusOptions={statusOpts}
          statusFilter={statusFilter}
          onStatusFilterChange={handleStatusChange}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onUndo={handleUndo}
          pagination
          itemsPerPage={itemsPerPage}
          currentPage={pagination?.page || 1}
          totalItems={pagination?.total || 0}
          setItemsPerPage={setItemsPerPage}
          onPageChange={handlePageChange}
          searchable
          searchTerm={searchInput}
          setSearchTerm={setSearchInput}
          onSearch={handleSearch}
          onAdd
          onAddClick={() => navigate("/team-member-form")} // Navigate for Add
          loading={loading}
          sortKey={sortKey}
          sortOrder={sortOrder}
          onSort={handleSort}
          draggable={true}
        />
      </DndContext>
      
      {selectedRow && (
        <ViewDataPopup
          open={viewPopupOpen}
          onClose={() => {
            setViewPopupOpen(false);
            setSelectedRow(null);
          }}
          data={selectedRow}
        />
      )}
    </div>
  );
};

export default TeamMember;
